import { categories } from "./constants";
import type { WebsiteLink } from "../components/WebsiteGrid";
import { getParentLocations, matchesParentLocation } from "./locationHelpers";

// === UTILITY FUNCTIONS ===

export function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

// === FILTER FUNCTIONS ===

export const matchesCategoryFilter = (
  link: WebsiteLink,
  categoryFilter?: string[],
): boolean => {
  if (!categoryFilter || categoryFilter.length === 0) {
    return true;
  }
  // Special handling for "Last Added" - check tags instead of categories
  if (categoryFilter.includes("Last Added")) {
    return link.tags?.some(tag => tag.startsWith("last")) ?? false;
  }
  // categoryFilter contains displayNames (e.g., ["Health • Sustainability"])
  // We need to check if the map has ANY of the constituent categories
  return categoryFilter.some((displayName) => {
    const categoryButton = categories.find(cat => cat.displayName === displayName);
    if (!categoryButton) return false;
    return categoryButton.categories.some((cat) => link.categories.includes(cat));
  });
};

export const matchesTagsFilter = (
  link: WebsiteLink,
  tagsFilter?: string[],
): boolean => {
  if (!tagsFilter || tagsFilter.length === 0) {
    return true;
  }
  // Use AND logic: map must have ALL selected tags
  return tagsFilter.every((selectedTag) => link.tags?.includes(selectedTag)) ?? false;
};

export const matchesFilters = (
  link: WebsiteLink,
  categoryFilter?: string[],
  tagsFilter?: string[],
): boolean => {
  return matchesCategoryFilter(link, categoryFilter) && matchesTagsFilter(link, tagsFilter);
};

// Internal helper - not exported as it's only used by aggregated count functions
const countMaps = (
  websiteLinks: WebsiteLink[],
  selectedContinent?: string,
  selectedCountry?: string,
  selectedCity?: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
): number => {
  return websiteLinks.filter((link) => {
    if (!matchesFilters(link, categoryFilter, tagsFilter)) {
      return false;
    }
    if (selectedContinent && link.continent !== selectedContinent) {
      return false;
    }
    if (selectedCountry && !link.countries?.includes(selectedCountry)) {
      return false;
    }
    if (selectedCity && !link.cities?.includes(selectedCity)) {
      return false;
    }
    return true;
  }).length;
};

// Get aggregated count for a continent (all maps in this continent and all its children)
export const getAggregatedContinentCount = (
  websiteLinks: WebsiteLink[],
  continent: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
) => {
  return countMaps(websiteLinks, continent, undefined, undefined, categoryFilter, tagsFilter);
};

// Get aggregated count for a country (all maps in this country and all its children)
export const getAggregatedCountryCount = (
  websiteLinks: WebsiteLink[],
  country: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
) => {
  return countMaps(websiteLinks, undefined, country, undefined, categoryFilter, tagsFilter);
};

// Get aggregated count for a city (all maps in this city)
export const getAggregatedCityCount = (
  websiteLinks: WebsiteLink[],
  city: string,
  categoryFilter?: string[],
  tagsFilter?: string[],
) => {
  return countMaps(websiteLinks, undefined, undefined, city, categoryFilter, tagsFilter);
};

// Get count for World Maps
export const getWorldMapsCount = (
  websiteLinks: WebsiteLink[],
  categoryFilter?: string[],
  tagsFilter?: string[]
) => {
  return websiteLinks.filter(link => link.world && matchesFilters(link, categoryFilter, tagsFilter)).length;
};

// === SECTIONED FILTERING (for Location Tab with hierarchy sections) ===

export interface MapSection {
  type: 'world' | 'continent' | 'country' | 'city';
  label: string;
  maps: WebsiteLink[];
  showOnlyMultiLocation: boolean;
}

/**
 * Get maps organized into sections based on the selected location hierarchy
 * Always returns sections in hierarchical order (World → Continent → Country → City)
 * @returns Array of sections, each containing maps of that level (unshuffled)
 */
export const getMapSections = (
  websites: WebsiteLink[],
  selectedCategories: string[],
  selectedContinents: string[],
  selectedCountries: string[],
  selectedCities: string[],
  selectedWorldMaps: boolean,
  selectedSearchTags: string[]
): MapSection[] => {
  const sections: MapSection[] = [];

  // Pre-calculate selected category button's constituent categories
  let selectedCategoryButtonCategories: string[] | undefined;
  let isLastAddedSelected = false;
  if (selectedCategories.length > 0) {
    const allConstituentCategories: string[] = [];
    selectedCategories.forEach(displayName => {
      if (displayName === "Last Added") {
        isLastAddedSelected = true;
      } else {
        const categoryButton = categories.find(cat => cat.displayName === displayName);
        if (categoryButton) {
          allConstituentCategories.push(...categoryButton.categories);
        }
      }
    });
    selectedCategoryButtonCategories = allConstituentCategories;
  }

  // Helper function to check if a map matches category and tag filters
  const matchesCategoryAndTags = (site: WebsiteLink): boolean => {
    // Category filter
    if (selectedCategories.length > 0) {
      let matchesCategory = false;
      
      if (isLastAddedSelected && site.tags?.some(tag => tag.startsWith("last"))) {
        matchesCategory = true;
      }
      
      if (selectedCategoryButtonCategories && selectedCategoryButtonCategories.length > 0) {
        if (selectedCategoryButtonCategories.some((cat) => site.categories.includes(cat))) {
          matchesCategory = true;
        }
      }
      
      if (!matchesCategory) {
        return false;
      }
    }

    // Search tags filter
    if (selectedSearchTags.length > 0) {
      if (!site.tags || !selectedSearchTags.every((tag) => site.tags.includes(tag))) {
        return false;
      }
    }

    return true;
  };

  // SCENARIO 1: World Maps selected
  if (selectedWorldMaps) {
    const worldMaps = websites.filter(site => 
      site.world === true && matchesCategoryAndTags(site)
    );
    // If categories or tags are selected, show section label for expandable UI
    const needsLabel = selectedCategories.length > 0 || selectedSearchTags.length > 0;
    return [{
      type: 'world',
      label: needsLabel ? 'World' : '',
      maps: worldMaps,
      showOnlyMultiLocation: false
    }];
  }

  // SCENARIO 2: City selected
  if (selectedCities.length > 0) {
    const city = selectedCities[0];
    const cityMaps = websites.filter(site =>
      site.cities?.some(c => selectedCities.includes(c)) && matchesCategoryAndTags(site)
    );
    
    // Always include parent-level maps in hierarchical order (now including World maps)
    const parentLocations = getParentLocations(city, websites);
    
    // World maps (top of hierarchy)
    const worldMaps = websites.filter(site => 
      site.world === true && matchesCategoryAndTags(site)
    );
    
    const continentMaps = websites.filter(site =>
      site.continent && parentLocations.includes(site.continent) &&
      (!site.countries || site.countries.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    const countryMaps = websites.filter(site =>
      site.countries?.some(c => parentLocations.includes(c)) &&
      (!site.cities || site.cities.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // Add sections in hierarchical order (World → Continent → Country → City)
    // Always add all sections, even if empty
    sections.push({
      type: 'world',
      label: 'World',
      maps: worldMaps,
      showOnlyMultiLocation: false
    });
    
    sections.push({
      type: 'continent',
      label: 'Continent',
      maps: continentMaps,
      showOnlyMultiLocation: false
    });
    
    sections.push({
      type: 'country',
      label: 'Country',
      maps: countryMaps,
      showOnlyMultiLocation: true  // Current level - only show badges on +N maps
    });
    
    // Add child level at the end
    sections.push({
      type: 'city',
      label: 'City',
      maps: cityMaps,
      showOnlyMultiLocation: false  // Lower level - show all badges
    });
    
    return sections;
  }

  // SCENARIO 3: Country selected
  if (selectedCountries.length > 0) {
    const country = selectedCountries[0];
    // Country-level maps (have countries but no cities)
    const countryMaps = websites.filter(site =>
      site.countries?.some(c => selectedCountries.includes(c)) &&
      (!site.cities || site.cities.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // City-level maps (have cities in the selected country)
    const cityMaps = websites.filter(site =>
      site.countries?.some(c => selectedCountries.includes(c)) &&
      site.cities && site.cities.length > 0 &&
      matchesCategoryAndTags(site)
    );
    
    // Always include parent-level maps in hierarchical order (now including World maps)
    const parentLocations = getParentLocations(country, websites);
    
    // World maps (top of hierarchy)
    const worldMaps = websites.filter(site => 
      site.world === true && matchesCategoryAndTags(site)
    );
    
    const continentMaps = websites.filter(site =>
      site.continent && parentLocations.includes(site.continent) &&
      (!site.countries || site.countries.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // Add sections in hierarchical order (World → Continent → Country → City)
    // Always add all sections, even if empty
    sections.push({
      type: 'world',
      label: 'World',
      maps: worldMaps,
      showOnlyMultiLocation: false
    });
    
    sections.push({
      type: 'continent',
      label: 'Continent',
      maps: continentMaps,
      showOnlyMultiLocation: false
    });
    
    sections.push({
      type: 'country',
      label: 'Country',
      maps: countryMaps,
      showOnlyMultiLocation: true  // Current level - only show badges on +N maps
    });
    
    // Add child level at the end
    sections.push({
      type: 'city',
      label: 'City',
      maps: cityMaps,
      showOnlyMultiLocation: false  // Lower level - show all badges
    });
    
    return sections;
  }

  // SCENARIO 4: Continent selected
  if (selectedContinents.length > 0) {
    const continent = selectedContinents[0];
    
    // World maps (top of hierarchy)
    const worldMaps = websites.filter(site => 
      site.world === true && matchesCategoryAndTags(site)
    );
    
    // Continent-level maps (have continent but no countries)
    const continentMaps = websites.filter(site =>
      selectedContinents.includes(site.continent || '') &&
      (!site.countries || site.countries.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // Country-level maps (have countries but no cities)
    const countryMaps = websites.filter(site =>
      selectedContinents.includes(site.continent || '') &&
      site.countries && site.countries.length > 0 &&
      (!site.cities || site.cities.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // City-level maps (have cities)
    const cityMaps = websites.filter(site =>
      selectedContinents.includes(site.continent || '') &&
      site.cities && site.cities.length > 0 &&
      matchesCategoryAndTags(site)
    );
    
    // Add sections in hierarchical order (World → Continent → Country → City)
    // Always add all sections, even if empty
    sections.push({
      type: 'world',
      label: 'World',
      maps: worldMaps,
      showOnlyMultiLocation: false
    });
    
    sections.push({
      type: 'continent',
      label: 'Continent',
      maps: continentMaps,
      showOnlyMultiLocation: true  // Current level - only show badges on +N maps
    });
    
    sections.push({
      type: 'country',
      label: 'Country',
      maps: countryMaps,
      showOnlyMultiLocation: false  // Lower level - show all badges
    });
    
    sections.push({
      type: 'city',
      label: 'City',
      maps: cityMaps,
      showOnlyMultiLocation: false  // Lower level - show all badges
    });
    
    return sections;
  }

  // SCENARIO 5: Only category/tags selected (no location) - show sections with all badges
  if (selectedCategories.length > 0 || selectedSearchTags.length > 0) {
    // World maps (no location data)
    const worldMaps = websites.filter(site =>
      site.world === true && matchesCategoryAndTags(site)
    );
    
    // Continent-level maps (have continent but no countries)
    const continentMaps = websites.filter(site =>
      site.continent &&
      (!site.countries || site.countries.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // Country-level maps (have countries but no cities)
    const countryMaps = websites.filter(site =>
      site.countries && site.countries.length > 0 &&
      (!site.cities || site.cities.length === 0) &&
      matchesCategoryAndTags(site)
    );
    
    // City-level maps (have cities)
    const cityMaps = websites.filter(site =>
      site.cities && site.cities.length > 0 &&
      matchesCategoryAndTags(site)
    );
    
    sections.push({
      type: 'world',
      label: 'World',
      maps: worldMaps,
      showOnlyMultiLocation: false  // No location badges needed (section header provides context)
    });
    
    sections.push({
      type: 'continent',
      label: 'Continent',
      maps: continentMaps,
      showOnlyMultiLocation: false  // Show all badges (continents are mixed)
    });
    
    sections.push({
      type: 'country',
      label: 'Country',
      maps: countryMaps,
      showOnlyMultiLocation: false  // Show all badges (countries are mixed)
    });
    
    sections.push({
      type: 'city',
      label: 'City',
      maps: cityMaps,
      showOnlyMultiLocation: false  // Show all badges (cities are mixed)
    });
    
    return sections;
  }

  // SCENARIO 6: No filters at all (default - show all maps in sections)
  // World maps
  const worldMaps = websites.filter(site =>
    site.world === true && matchesCategoryAndTags(site)
  );
  
  // Continent-level maps (have continent but no countries)
  const continentMaps = websites.filter(site =>
    site.continent &&
    (!site.countries || site.countries.length === 0) &&
    matchesCategoryAndTags(site)
  );
  
  // Country-level maps (have countries but no cities)
  const countryMaps = websites.filter(site =>
    site.countries && site.countries.length > 0 &&
    (!site.cities || site.cities.length === 0) &&
    matchesCategoryAndTags(site)
  );
  
  // City-level maps (have cities)
  const cityMaps = websites.filter(site =>
    site.cities && site.cities.length > 0 &&
    matchesCategoryAndTags(site)
  );
  
  sections.push({
    type: 'world',
    label: 'World',
    maps: worldMaps,
    showOnlyMultiLocation: false
  });
  
  sections.push({
    type: 'continent',
    label: 'Continent',
    maps: continentMaps,
    showOnlyMultiLocation: false
  });
  
  sections.push({
    type: 'country',
    label: 'Country',
    maps: countryMaps,
    showOnlyMultiLocation: false
  });
  
  sections.push({
    type: 'city',
    label: 'City',
    maps: cityMaps,
    showOnlyMultiLocation: false
  });
  
  return sections;
};

/**
 * Shuffles the maps within each section
 * This separates the presentation concern (shuffling) from filtering logic
 * @param sections - Array of map sections to shuffle
 * @returns New array with shuffled maps in each section
 */
export const shuffleSectionMaps = (sections: MapSection[]): MapSection[] => {
  return sections.map(section => ({
    ...section,
    maps: shuffleArray(section.maps)
  }));
};